/**
 * Shared types and formatters for the mission *list* surfaces — the dispatch board
 * (`/missions`) and the history log (`/missions/history`).
 *
 * The per-mission detail surfaces use `missionDetailShared.ts`; status labels and badge
 * mapping are re-exported from there so there is a single source of truth for both.
 */
export { STATUS_LABELS, STATUS_BADGE_MAP } from './missionDetailShared';
// The hold-aware badge tone and the "is anything actually flying" predicate live with the
// badge map for the same reason it does: one source of truth for both list and detail
// surfaces, so the COP row and the mission page cannot describe one mission differently.
export { missionBadgeStatus, missionSortieAirborne, missionSortieState, type MissionCoverageHold } from './missionDetailShared';
import type { MissionType } from '@/lib/mission-type';
import { DEFAULT_TIMEZONE, formatDateOnly, formatDateTime } from '@/lib/dateFormat';
import { COVERAGE_UNREAD_COPY } from '@/lib/copy/status';

/** A mission as returned by `GET /api/missions` (list shape — waypoint count, no waypoints). */
export interface MissionListItem {
  id: string;
  name: string;
  description: string | null;
  status: string;
  /** 022. What the mission IS. NOT NULL server-side with a DEFAULT, so always present. */
  type: MissionType;
  /**
   * 022, ORBIT only. The circle's size and which way round it goes.
   *
   * On the payload since 022 (`GET /api/missions` selects `m.*`) and declared here because
   * without them an orbit is indistinguishable from a GO TO POINT on every list in the product:
   * one stored point, no radius, no direction. For this one type the parameters ARE the flight
   * path, so a row that omits them is not describing the sortie.
   */
  orbit_radius_m?: number | null;
  orbit_direction?: 'cw' | 'ccw' | null;
  scheduled_at: string | null;
  estimated_duration_minutes: number | null;
  default_altitude: number;
  default_speed: number;
  total_distance_meters: number | null;
  drone_callsign: string | null;
  pilot_name: string | null;
  site_name: string | null;
  waypoint_count: number;
  assigned_drone_id: string | null;
  assigned_pilot_id: string | null;
  site_id: string | null;
  created_at: string;
  /** Set by the `set_missions_updated` trigger on every write, including status transitions. */
  updated_at: string;
  /**
   * Continuous coverage is holding this mission open with NOTHING IN THE AIR — it has flown at
   * least one sortie, all of them have landed, and it stays `in_progress` until an operator
   * launches the next aircraft in the rotation. Server-computed; see `MissionCoverageHold` in
   * `missionDetailShared.ts` for why it cannot be derived here and why it is optional.
   *
   * Read it through `missionSortieAirborne` / `missionBadgeStatus`, never directly — the point
   * of the field is that `status === 'in_progress'` alone is not evidence of an aircraft in the
   * air, and a surface that reaches past those helpers is free to make that mistake again.
   */
  coverage_held_open?: boolean;
  /**
   * Continuous coverage is attached to this mission. The list uses it for one thing: not
   * offering a delete the server now refuses (#391). See `missionCoverageLock`.
   */
  coverage_attached?: boolean;
}

/**
 * What deleting a mission actually removes, for the confirmation dialog.
 *
 * IT USED TO SAY "All their waypoints will be removed", which was true and badly incomplete: five
 * foreign keys point at a `missions` row. Four cascade — `mission_waypoints`, `mission_coverage`
 * and its `mission_rotation_members`, and the `rotation_actions` / `mission_segments` audit pair —
 * and `flights.mission_id` is set to NULL, permanently, on an append-only table. An operator
 * clearing old missions was told they were deleting routes while a station's rotation history
 * went with them.
 *
 * `db/migrations/025_coverage.sql` is explicit that this is intended ("Everything still dies with
 * the mission itself"), so the fix is to SAY it, not to prevent it. What must not happen — a
 * delete that walks around the airborne refusal, or that strands a live sortie — is
 * `missionDeleteBlocker` below.
 *
 * Phrased for both the one-mission and the many-mission wording.
 */
export const DELETE_CONSEQUENCE =
  'Waypoints and any coverage history (rotation actions, station segments) go too. '
  + 'Flight records are kept, but lose their link to the mission.';

/**
 * Why this mission cannot be deleted, in the operator's words — or null when it can be (#391).
 *
 * THE SERVER IS THE GATE; THIS IS THE ANNOUNCEMENT. `DELETE /api/missions/:id` refuses both of
 * these with a 409, and it refuses them because the delete cascade reaches further than the
 * dialog ever admitted: deleting an `in_progress` mission severs its live flight's `mission_id`
 * for ever (`flights` is append-only), and deleting a covered one walks around the airborne
 * refusal that `detachCoverage` exists to make. The list button is disabled so the refusal is
 * read before the press, not as a toast after it.
 *
 * ORDER MATTERS. A covered mission is usually also `in_progress`, and "detach coverage" is the
 * more actionable of the two — but it is not the one that is true first: an in-progress mission
 * has a sortie bound to it whether or not coverage is attached, and telling an operator to
 * detach coverage they may not have would send them to the wrong panel. Status first.
 */
export function missionDeleteBlocker(m: { status: string; coverage_attached?: boolean }): string | null {
  if (m.status === 'in_progress') return 'In progress — a sortie is bound to this mission';
  if (m.coverage_attached === true) return 'Coverage attached — detach it first';
  /**
   * ABSENT IS NOT "NO" (#471). The server omits this field when its coverage lookup failed, and
   * it used to read here as "nothing attached, go ahead" — offering a delete that
   * `DELETE /api/missions/:id` then answered with a 409, because the server does its own lookup
   * and refuses when THAT throws.
   *
   * SO THE UNKNOWN BLOCKS, and the cost of each direction is not symmetric. Blocking costs a
   * transient refusal on a mission that may well have been deletable; the operator retries in
   * ten seconds. Not blocking costs a destructive action offered and then refused, which is
   * exactly what this file's standing rule — refused before the press, not after — exists to
   * stop, and it is worse on a control whose whole job is deleting rotation evidence.
   *
   * It gets its OWN sentence rather than reusing "Coverage attached": that one tells the
   * operator to go and detach something that may not exist, which sends them to the wrong panel
   * and makes the platform look wrong when they find nothing there.
   */
  if (m.coverage_attached === undefined) return COVERAGE_UNREAD_COPY.retry;
  return null;
}

/**
 * How many missions in a list the server could not answer coverage for, out of how many (#650).
 *
 * THE SYSTEMIC CONDITION, WHICH NO SINGLE ROW CAN STATE. Each row already says "coverage unread"
 * for itself — the badge, the delete gate, the terminal lock. What none of them can say is how
 * WIDE it is, and that is the one thing that changes what an operator does: one gray badge among
 * sixty reads as "something is wrong with THIS mission" and is worth a retry, while the whole
 * board going gray means the platform cannot answer at all and the aircraft is the only source
 * left. `COVERAGE_UNREAD_COPY.boardNotice` says which, off this tally.
 *
 * A COUNT AND NOT A BOOLEAN, because the two faults the server can have are very different sizes
 * and produce the same flag. `withCoverageFacts` settles the two reads independently, on purpose:
 * a failed `attached` lookup clouds every row, while a failed `held` lookup clouds only the
 * `in_progress` ones — so on a library of drafts and one running sortie, a bare boolean would put
 * "the platform cannot read coverage" over a board where fifty-nine rows are answered and every
 * delete still works.
 *
 * BOTH FIELDS COUNT. `coverage_held_open` is `false` BY DEFINITION on any status other than
 * `in_progress` (`withCoverageFacts` never asks about one), so `undefined` there is unambiguous
 * evidence of a failed lookup and cannot fire on an ordinary board.
 *
 * An empty list is not an outage: there is nothing that could have been read.
 */
export interface CoverageReadTally {
  /** Missions carrying at least one coverage field the server could not determine. */
  unread: number;
  /** Missions considered. `unread === total` is the systemic case the banner words differently. */
  total: number;
}

export function tallyCoverageReads(
  missions: ReadonlyArray<{ coverage_attached?: boolean; coverage_held_open?: boolean }>,
): CoverageReadTally {
  const unread = missions.filter(
    (m) => m.coverage_attached === undefined || m.coverage_held_open === undefined,
  ).length;
  return { unread, total: missions.length };
}

/** Nothing read yet, and nothing to read — the initial state of every board's tally. */
export const NO_COVERAGE_READS: CoverageReadTally = { unread: 0, total: 0 };

/** Statuses that belong on the planning board (the live strip handles `in_progress`). */
export const BOARD_STATUSES = ['draft', 'scheduled'] as const;

/** Statuses that belong in the history log. */
export const HISTORY_STATUSES = ['completed', 'cancelled'] as const;

/** The mission's planning surface. One helper so row, name link and Open button cannot drift. */
export function missionDetailHref(missionId: string): string {
  return `/missions/${missionId}`;
}

/**
 * True when a click inside a mission row landed on something that handles itself, so a
 * row-level navigation must stand down.
 *
 * `Element`, not `HTMLElement`: every control in the actions cell renders a lucide `<svg>`,
 * and an `SVGElement` slips past an `HTMLElement` check — which would navigate on top of the
 * control's own action (a duplicate history entry for the links, a dialog plus a navigation
 * for the buttons).
 *
 * `[data-row-actions]` covers the actions cell as a whole rather than just its current
 * contents. That matters for the disabled Start button: `disabled:pointer-events-none` means
 * the click lands on the cell instead of the button, so an element-level check alone would
 * navigate away from a mission the operator is mid-way through starting. It also makes any
 * control added to that cell later safe by default.
 */
export function isRowActivationTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('a, button, [data-row-actions]') !== null;
}

/** 24h timestamp in the org's display timezone (defaults to UTC), e.g. "Jul 24, 14:00".
 *  Returns "-" for null/unparseable input. */
export function formatMissionTime(iso: string | null, timezone: string = DEFAULT_TIMEZONE): string {
  if (!iso) return '-';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '-';
  return formatDateTime(ms, timezone);
}

/** Calendar date in the org's display timezone (defaults to UTC), e.g. "Jul 24, 2026".
 *  Returns "-" for null/unparseable input. */
export function formatMissionDate(iso: string | null, timezone: string = DEFAULT_TIMEZONE): string {
  if (!iso) return '-';
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '-';
  return formatDateOnly(ms, timezone);
}

/**
 * Elapsed clock since `fromIso`, as `MM:SS` (or `H:MM:SS` past an hour).
 * Returns "--:--" when the timestamp is missing or unparseable.
 *
 * A timestamp slightly in the future is clock skew between the API host and the browser, not a
 * real negative duration — clamp it to `00:00` rather than blanking the field at the exact
 * moment an operator is watching a mission go live.
 */
export function formatElapsed(fromIso: string | null, nowMs: number): string {
  if (!fromIso) return '--:--';
  const startMs = new Date(fromIso).getTime();
  if (!Number.isFinite(startMs)) return '--:--';
  const totalSeconds = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
